import React, { useRef, useState, useEffect } from 'react';
import '../styles/TrainingVideoSection.css';

const extractYoutubeId = (input) => {
  if (!input) return '';
  if (/^[a-zA-Z0-9_-]{11}$/.test(input)) return input;
  try {
    const url = new URL(input);
    if (url.hostname === 'youtu.be') return url.pathname.slice(1).split('/')[0];
    return url.searchParams.get('v') || input;
  } catch {
    return input;
  }
};

export default function TrainingVideoSection({ section, onReady }) {
  const vc = section?.videoContent || {};
  const youtubeId = extractYoutubeId(vc.youtubeId?.trim());
  const required = vc.requiredPercent ?? 85;

  const [watched, setWatched] = useState(0);
  const [volume, setVolume] = useState(80);
  const [muted, setMuted] = useState(false);
  const playerRef = useRef(null);
  const intervalRef = useRef(null);
  const readyFiredRef = useRef(false);
  const maxReachedRef = useRef(0);
  const containerRef = useRef(null);  // div wrapper controlado por React
  const onReadyRef = useRef(onReady);
  useEffect(() => { onReadyRef.current = onReady; });

  const handleVolumeChange = (val) => {
    setVolume(val);
    if (playerRef.current) {
      playerRef.current.setVolume(val);
      if (val === 0) playerRef.current.mute();
      else { playerRef.current.unMute(); setMuted(false); }
    }
  };

  const toggleMute = () => {
    if (!playerRef.current) return;
    if (muted) { playerRef.current.unMute(); playerRef.current.setVolume(volume || 80); setMuted(false); }
    else { playerRef.current.mute(); setMuted(true); }
  };

  useEffect(() => {
    if (!youtubeId) {
      return;
    }


    const createPlayer = () => {
      if (playerRef.current) return;
      if (!containerRef.current) return;

      // Crear un div hijo manualmente — YT lo reemplazará con un iframe
      // sin tocar el div wrapper que React controla
      const mountPoint = document.createElement('div');
      mountPoint.style.width = '100%';
      mountPoint.style.height = '100%';
      containerRef.current.appendChild(mountPoint);

      playerRef.current = new window.YT.Player(mountPoint, {
        videoId: youtubeId,
        playerVars: { origin: window.location.origin, rel: 0, controls: 0, disablekb: 1 },
        events: {
          onReady: (e) => {
            const iframe = e.target.getIframe();
            iframe.style.width = '100%';
            iframe.style.height = '100%';
            iframe.style.display = 'block';
            playerRef.current.setVolume(80);
          },
          onStateChange: (e) => {
            if (e.data === window.YT.PlayerState.PLAYING) {
              intervalRef.current = setInterval(() => {
                if (!playerRef.current) return;
                const duration = playerRef.current.getDuration();
                const current = playerRef.current.getCurrentTime();
                if (duration > 0) {
                  // Anti-seek: si el usuario saltó hacia adelante, volver al máximo alcanzado
                  if (current > maxReachedRef.current + 2) {
                    playerRef.current.seekTo(maxReachedRef.current, true);
                  } else if (current > maxReachedRef.current) {
                    maxReachedRef.current = current;
                  }
                  const pct = Math.round((maxReachedRef.current / duration) * 100);
                  setWatched(pct);
                  if (pct >= required && !readyFiredRef.current) {
                    readyFiredRef.current = true;
                    onReadyRef.current?.();
                    clearInterval(intervalRef.current);
                  }
                }
              }, 1000);
            } else {
              clearInterval(intervalRef.current);
            }
          }
        }
      });
    };

    if (typeof window.YT === 'undefined') {
      if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
        const tag = document.createElement('script');
        tag.src = 'https://www.youtube.com/iframe_api';
        document.head.appendChild(tag);
      }
      window.onYouTubeIframeAPIReady = createPlayer;
    } else {
      createPlayer();
    }

    return () => {
      clearInterval(intervalRef.current);
      if (playerRef.current?.destroy) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
    };
  }, [youtubeId, required]);

  if (!youtubeId) return (
    <div className="text-muted fst-italic p-3 text-center">
      <i className="fas fa-video-slash me-2" />
      Sin vídeo configurado
    </div>
  );

  return (
    <div className="d-flex flex-column align-items-center">
      <div className="tvs-video-wrapper mb-3 w-100">
        <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
      </div>
      <div className="tvs-controls d-flex align-items-center gap-3 small text-muted mt-2 w-100">
        <div className="tvs-volume-group">
          <button
            className="btn btn-sm btn-outline-secondary p-1 px-2"
            onClick={toggleMute}
            title={muted ? 'Activar sonido' : 'Silenciar'}
          >
            <i className={`fas ${muted || volume === 0 ? 'fa-volume-xmark' : volume < 50 ? 'fa-volume-low' : 'fa-volume-high'}`} />
          </button>
          <input
            type="range" min="0" max="100" value={muted ? 0 : volume}
            onChange={(e) => handleVolumeChange(Number(e.target.value))}
            title={`Volumen: ${muted ? 0 : volume}%`}
          />
        </div>
        <div className="flex-grow-1">
          <div className="progress" style={{ height: 6 }}>
            <div
              className="progress-bar bg-primary"
              style={{ width: `${watched}%`, transition: 'width 0.5s' }}
            />
          </div>
        </div>
        <span>{watched}% visto (mínimo {required}%)</span>
        {watched >= required && (
          <span className="text-success fw-semibold">
            <i className="fas fa-check me-1" />Listo
          </span>
        )}
        {/* TEST ONLY */}
        <button className="btn btn-sm btn-warning" onClick={() => { setWatched(100); onReady && onReady(); }}>
          Skip
        </button>
      
      </div>
    </div>
  );
}